import {
  ENDPOINTS,
  RESOURCE_TYPE_STAFFER,
  RESOURCE_TYPE_APPLIANCE,
  BUSINESS_STATUSES,
  PAYMENT_SOURCES,
  KYC_STATUSES,
  PAY_BY_APP_STATUSES,
  FEATURE_FLAGS,
  BUSINESS_BOOST_STATUSES,
  BOOKING_MODES,
} from '@/lib/consts.ts'

import { useEmitter } from '@/composables/emitter'

import { DEFAULT_OPENING_HOURS } from '@/components/staff/shifts/consts'
import { ONBOARDING_STATUSES } from '@/components/settings/payments/payment-types/booksy-card-reader/consts'
import { __t } from '@/lib/helpers'

import { fastPayoutsEnabledFor } from '@/components/fast-payouts/functions.js'

import {
  getBusiness,
  getUserBusinesses,
  getBooksyPaySettings,
} from '@/api'

import { PAYMENT_TYPE, PLATFORM_FEE_MODES } from '@/components/sales/checkout/consts'
import { useEppo } from '@/composables/eppo'
import { useDatadogLoginActions, DATADOG_LOGIN_ENDPOINTS } from '@/components/onboarding/composables/datadog-login-actions/index.js'
import {
  compose, filter, propEq, path, find, indexBy, prop, map,
  ifElse, isNil, always, identity, clone,
} from 'ramda'

export default {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: {
    business: null,
    businessesCount: 1,
    resources: [],
    resourcesCount: 0,
    serviceCategories: [],
    pos: null,
    stripeAccount: null,
    stripeAvailableBalance: null,
    stripeFastPayoutEnabledByMerchant: null,
    stripePayoutAccount: null,
    stripePayoutLimits: null,
    boostStatusData: null,
    billingSettings: null,
    boostAvailable: false,
    businessVerified: false,
    billingAccess: null,
    serviceColorPalette: null,
    wereServicesEditedInCurrentSession: false,
    partnerApps: [],
    partnerAppsCatalog: [],
    serviceTypeMarketing: null,
    stripeAccountHolderInfo: null,
    certificationSettings: null,
    disableSettingsBar: false,
    booksyPaySettings: null,
  },

  /*!
   *
   * actions
   *
   */
  actions: {

    /*
     * do NOT call manually - called on logout
     * @see: main.js
     */
    reset ({ commit, dispatch }) {
      commit('setBusiness', null)
      commit('setBusinessesCount', 1)
      commit('setResources', { resources: [], resourcesCount: 0 })
      commit('setServiceCategories', { serviceCategories: [] })
      commit('setPos', { pos: null })
      commit('setStripeAccount', { stripeAccount: null })
      commit('setStripeAvailableBalance', { stripeAvailableBalance: null })
      commit('setStripePayoutAccount', { stripePayoutAccount: null })
      commit('setBoostStatusData', { boostStatusData: null })
      commit('setBoostAvailable', { boostAvailable: false })
      commit('setDisableSettingsBar', false)
      commit('setBillingAccess', { billingAccess: null })
      commit('setServiceColorPalette', { serviceColorPalette: null })
      commit('setPartnerApps', { partnerApps: [] })
      commit('setPartnerAppsCatalog', [])
      dispatch('subscription/reset', null, { root: true })
      commit('setCertificationSettings', { certificationSettings: null })
      commit('setCurrentStaffer', { certificationSettings: null })
      commit('setServicesEditedInCurrentSession', { wereServicesEditedInCurrentSession: false })
      commit('setBooksyPaySettings', { booksyPaySettings: null })
    },

    autoloadBusiness ({ rootState, commit, dispatch }) {
      const { reportLoginAction } = useDatadogLoginActions()

      return new Promise(async (resolve, reject) => {
        getUserBusinesses()
          .then(({ data }) => {
            const {
              businesses,
              businesses_count: businessCount,
            } = data
            const { user } = rootState.identity

            commit('setBusinessesCount', businessCount)

            let business = null

            if (user.businessId) {
              business = { id: user.businessId }
            }

            if (!business) {
              // find first active...
              business = businesses.find(({ active }) => active)
            }

            if (!business) {
              // take first one...
              business = businesses[0]
            }
            const businessId = business ? business.id : data.businesses[0]?.id

            if (!businessId) {
              reportLoginAction({
                isWorkingProperly: true,
                info: DATADOG_LOGIN_ENDPOINTS.BUSINESSES_LIST,
              })
              return reject(new Error('no business id'))
            }

            // load current business
            dispatch('loadBusiness', businessId)
              .then((data) => {
                reportLoginAction({
                  isWorkingProperly: true,
                  info: DATADOG_LOGIN_ENDPOINTS.BUSINESS_DETAILS,
                })
                resolve(data)
              })
              .catch((error) => {
                reportLoginAction({
                  isWorkingProperly: false,
                  response: error?.response,
                  info: DATADOG_LOGIN_ENDPOINTS.BUSINESS_DETAILS,
                })
                reject(error)
              })
          })
          .catch((error) => {
            reportLoginAction({
              isWorkingProperly: false,
              response: error?.response,
              info: DATADOG_LOGIN_ENDPOINTS.BUSINESSES_LIST,
            })
            reject(error)
          })
      })
    },

    async updateBusiness ({ commit, dispatch }, data) {
      // overwrite permissions created in `identity/login` action
      if (data?.current_staffer?.staff_access_level) {
        await dispatch('identity/setStafferAccessLevel',
          data.current_staffer.staff_access_level,
          { root: true }
        )
      }

      // set business
      commit('setBusiness', {
        ...data.business,
        current_staffer: data.current_staffer,
        business_opening_hours: data.business.business_opening_hours?.length
          ? data.business.business_opening_hours
          : DEFAULT_OPENING_HOURS,
        service_type_marketing: data.service_type_marketing,
      })

      // set serviceColorPalette
      commit('setServiceColorPalette', {
        serviceColorPalette: data.service_color_palette,
      })

      await dispatch('identity/selectBusiness',
        data.business.id,
        { root: true }
      )

      // load resources
      return dispatch('loadRelated')
    },

    loadBusiness ({ dispatch }, businessId) {
      return new Promise((resolve, reject) => {
        // load business
        getBusiness(businessId, { retries: 10 }).then(({ data }) => {
          dispatch('updateBusiness', data)
            .then(resolve)
            .catch(reject)
        })
          .catch(reject)
      })
    },

    reloadBusiness ({ state, commit, dispatch }) {
      return dispatch('loadBusiness', state.business.id)
    },

    loadRelated ({ state, commit, rootState, rootGetters, dispatch }) {
      const related = [
        dispatch('loadResources'),
        dispatch('loadServiceCategories'),
        dispatch('loadPos'),
      ]

      // https://booksy.atlassian.net/browse/PET-2308
      if (rootState.app.country === 'fr') {
        related.push(dispatch('loadCertificationSettings'))
      }

      const redirectToInvDetailsFlagEnabled = useEppo().ff(FEATURE_FLAGS.Feature_ForceConfirmBillingInvoiceDataFlag)

      if (rootGetters['identity/isManagerOrHigher']) {
        if (state.business?.status !== BUSINESS_STATUSES.SETUP) {
          if (rootGetters['subscription/useLegacySubscription']) {
            related.push(dispatch('subscription/loadSubscriptionLegacy', null, { root: true }))
          } else {
            related.push(dispatch('subscription/loadSubscriptionBilling', null, { root: true }))
          }
          related.push(dispatch('subscription/loadBillingSettings', null, { root: true }))
          if (redirectToInvDetailsFlagEnabled) {
            related.push(dispatch('subscription/getSubscriptionBuyerDataV2', null, { root: true }))
          }
        }

        if (state.business?.status !== BUSINESS_STATUSES.SETUP
          && state.business.payment_source === PAYMENT_SOURCES.UNKNOWN
        ) {
          related.push(dispatch('loadBooksyBillingAccess'))
        }
      }

      if (state.business.payment_source !== PAYMENT_SOURCES.OFFLINE) {
        // load additional overdue data for new vesion of Payment Method
        if ([
          BUSINESS_STATUSES.BLOCKED_OVERDUE,
          BUSINESS_STATUSES.CHURNED,
          BUSINESS_STATUSES.PAID_ACTIVE,
          BUSINESS_STATUSES.ACTIVE_OVERDUE,
        ].includes(state.business.status)) {
          related.push(
            dispatch('subscription/loadOverduePaymentsTotal',
              null, { root: true }
            ))
        }

        // load additional overdue data for overdued business
        if ([
          BUSINESS_STATUSES.BLOCKED_OVERDUE,
          BUSINESS_STATUSES.ACTIVE_OVERDUE,
        ].includes(state.business.status)) {
          related.push(
            dispatch('subscription/loadOverduePaymentsDetails',
              null, { root: true })
          )
        }
      }

      return Promise.all(related)
    },

    loadResources ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadResources, called while business is not set.')
      }

      return dispatch('api/call', {
        url: ENDPOINTS.RESOURCES,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setResources', {
            resources: data.resources,
            resourcesCount: data.resources_count,
          })
        })
        .catch(e => {
          // reject if business is active
          // business inactive (onboarding) ignore error
          if (state.business.active) {
            return Promise.reject(e)
          }
        })
    },

    async loadServiceCategories ({ state, commit, dispatch }) {
      // temporary solution due to BP-2085
      const serviceCategoriesTimeout = 20000

      if (!state.business) {
        throw new Error('Store action: business.loadServiceCategories, called while business is not set.')
      }

      return dispatch('api/call', {
        url: ENDPOINTS.SERVICE_CATEGORIES,
        urlParams: { businessId: state.business.id },
        timeout: serviceCategoriesTimeout,
        params: {
          with_combos: true,
        },
      }, { root: true })
        .then(({ data }) => {
          commit('setServiceCategories', {
            serviceCategories: data.service_categories,
            serviceTypeMarketing: data.service_type_marketing,
          })
        })
        .catch(e => {
          // reject if business is active
          // business inactive (onboarding) ignore error
          if (state.business.active) {
            return Promise.reject(e)
          }
        })
    },

    loadPos ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadPos, called while business is not set.')
      }

      return dispatch('api/call', {
        url: ENDPOINTS.POS,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setPos', {
            pos: data.pos,
          })
        })
        .catch(e => {
          // reject if business is active
          // business inactive (onboarding) ignore error
          if (state.business.active) {
            return Promise.reject(e)
          }
        })
    },

    loadStripeAccount ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadStripeAccount, called while business is not set.')
      }

      if (!state.pos?.force_stripe_pba) {
        return
      }

      return dispatch('api/call', {
        url: ENDPOINTS.STRIPE_ACCOUNT,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setStripeAccount', {
            stripeAccount: data,
          })
        })
    },

    loadStripePayoutAccount ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadStripePayoutAccount, called while business is not set.')
      }

      return dispatch('api/call', {
        url: ENDPOINTS.STRIPE_PAYOUT_ACCOUNT_DETAILS,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setStripePayoutAccount', {
            stripePayoutAccount: data,
          })
        })
    },

    loadStripeAvailableBalance ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadStripeAvailableBalance, called while business is not set.')
      }
      return dispatch('api/call', {
        url: ENDPOINTS.STRIPE_AVAILABLE_BALANCE,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setStripeAvailableBalance', {
            stripeAvailableBalance: data,
          })
        })
    },

    loadStripeFastPayoutEnabledByMerchant ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadStripeFastPayoutEnabledByMerchant, called while business is not set.')
      }

      return dispatch('api/call', {
        url: ENDPOINTS.POS_FAST_PAYOUT_ENABLED_BY_MERCHANT,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setStripeFastPayoutEnabledByMerchant', {
            stripeFastPayoutEnabledByMerchant: data?.fast_payouts_merchant_enabled,
          })
        })
    },

    loadStripePayoutLimits ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadStripePayoutLimits, called while business is not set.')
      }
      return dispatch('api/call', {
        url: ENDPOINTS.STRIPE_PAYOUT_LIMITS,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setStripePayoutLimits', {
            stripePayoutLimits: data,
          })
        })
    },

    async loadFastPayoutData ({ state, dispatch }) {
      await Promise.all([
        dispatch('loadStripeAccount'),
        dispatch('loadStripeAvailableBalance'),
      ])

      if (fastPayoutsEnabledFor(state.stripeAccount?.fast_payouts_status)) {
        return dispatch('loadStripePayoutAccount')
      }
    },

    getBoostStatus ({ state, commit, dispatch }) {
      return dispatch('api/call', {
        url: ENDPOINTS.BOOST_STATUS,
        urlParams: {
          businessId: state.business.id,
        },
      }, { root: true })
        .then(({ data }) => {
          commit('setBoostStatusData', {
            boostStatusData: data,
          })
          commit('setBoostAvailable', {
            boostAvailable: data.boost_status.status !== 'disabled',
          })
        })
        .catch(error => {
          if (error.response.status !== 400) { // 79907 - Status 400 means, that boost is not available
            throw error
          }
        })
    },

    setBoostStatus ({ commit }, payload) {
      commit('setBoostStatus', payload)
    },

    getBusinessVerifiedStatus ({ commit, dispatch, state }) {
      return dispatch('api/call', {
        url: ENDPOINTS.BOOST_BUSINESS_VERIFIED,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setBusinessVerified', { businessVerified: data.verified })
        })
    },

    loadBooksyBillingAccess ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadBooksyBillingAccess, called while business is not set.')
      }

      return dispatch('api/call', {
        method: 'POST',
        url: ENDPOINTS.BILLING_CHECK_ACCESS,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setBillingAccess', {
            billingAccess: data,
          })

          return true
        })
    },

    async loadStripeAccountHolderInfo ({ state, commit, dispatch }) {
      const { data: stripeAccountHolderInfo } = await dispatch('api/call', {
        method: 'GET',
        url: ENDPOINTS.STRIPE_ACCOUNT_HOLDER_INFO,
        urlParams: { businessId: state.business.id },
      }, { root: true })

      commit('setStripeAccountHolderInfo', {
        stripeAccountHolderInfo,
      })
    },

    async updateStripeAccountHolderInfo ({ state, commit, dispatch }, payload) {
      const { data: stripeAccountHolderInfo } = await dispatch('api/call', {
        method: 'PUT',
        url: ENDPOINTS.STRIPE_ACCOUNT_HOLDER_INFO,
        urlParams: { businessId: state.business.id },
        data: payload,
      }, { root: true })

      commit('setStripeAccountHolderInfo', {
        stripeAccountHolderInfo,
      })
    },

    loadCertificationSettings ({ state, commit, dispatch }) {
      if (!state.business) {
        throw new Error('Store action: business.loadCertificationSettings, called while business is not set.')
      }

      return dispatch('api/call', {
        url: ENDPOINTS.CERTIFICATION_SETTINGS,
        urlParams: { businessId: state.business.id },
      }, { root: true })
        .then(({ data }) => {
          commit('setCertificationSettings', {
            certificationSettings: data,
          })
        })
    },

    async loadBooksyPaySettings ({ state, commit, dispatch, rootState, rootGetters }) {
      if (!state.business) {
        throw new Error('Store action: business.loadBooksyPaySettings, called while business is not set.')
      }

      if (!rootGetters['routingConfig/shouldUseBooksyPay']) {
        return
      }

      const { data } = await getBooksyPaySettings(state.business.id)
      commit('setBooksyPaySettings', { booksyPaySettings: data })
    },
  },

  /*!
   *
   * mutations
   *
   */
  mutations: {

    setBusiness (state, business) {
      state.business = business
      const { emit, EVENTS } = useEmitter()
      emit(EVENTS.BUSINESS_STATE_CHANGE, business)
    },

    setBusinessesCount (state, count) {
      state.businessesCount = count
    },

    setResources (state, { resources, resourcesCount }) {
      state.resources = resources
      state.resourcesCount = resourcesCount
    },

    setServiceCategories (state, { serviceCategories, serviceTypeMarketing }) {
      state.serviceCategories = serviceCategories
      state.serviceTypeMarketing = serviceTypeMarketing
    },

    setPos (state, { pos }) {
      state.pos = pos
    },

    setStripeAccount (state, { stripeAccount }) {
      state.stripeAccount = stripeAccount
    },

    setStripeAvailableBalance (state, { stripeAvailableBalance }) {
      state.stripeAvailableBalance = stripeAvailableBalance
    },

    setStripeFastPayoutEnabledByMerchant (state, { stripeFastPayoutEnabledByMerchant }) {
      state.stripeFastPayoutEnabledByMerchant = stripeFastPayoutEnabledByMerchant
    },

    setStripePayoutAccount (state, { stripePayoutAccount }) {
      state.stripePayoutAccount = stripePayoutAccount
    },

    setStripePayoutLimits (state, { stripePayoutLimits }) {
      state.stripePayoutLimits = stripePayoutLimits
    },

    setBoostStatusData (state, { boostStatusData }) {
      state.boostStatusData = boostStatusData
    },

    setBoostStatus (state, { boostStatus }) {
      state.boostStatusData.boost_status.status = boostStatus
    },

    setBoostAvailable (state, { boostAvailable }) {
      state.boostAvailable = boostAvailable
    },

    setDisableSettingsBar (state, disableSettingsBar) {
      state.disableSettingsBar = disableSettingsBar
    },

    setBusinessVerified (state, { businessVerified }) {
      state.businessVerified = businessVerified
    },

    setBillingAccess (state, { billingAccess }) {
      state.billingAccess = billingAccess
    },

    setServiceColorPalette (state, { serviceColorPalette }) {
      state.serviceColorPalette = serviceColorPalette
    },
    setPartnerApps (state, { partnerApps }) {
      if (state.business) {
        state.business.partner_apps = partnerApps
      }
    },

    setPartnerAppsCatalog (state, partnerAppsCatalog) {
      state.partnerAppsCatalog = partnerAppsCatalog
    },

    setStripeAccountHolderInfo (state, { stripeAccountHolderInfo }) {
      state.stripeAccountHolderInfo = stripeAccountHolderInfo
    },

    setCertificationSettings (state, { certificationSettings }) {
      state.certificationSettings = certificationSettings
    },

    setServicesEditedInCurrentSession (state, servicesEdited) {
      state.wereServicesEditedInCurrentSession = servicesEdited
    },

    setBooksyPaySettings (state, { booksyPaySettings }) {
      state.booksyPaySettings = booksyPaySettings
    },
  },

  /*
   *
   * getters
   *
   */
  getters: {

    staffers: compose(
      filter(propEq(RESOURCE_TYPE_STAFFER, 'type')),
      path(['resources'])
    ),

    appliances: compose(
      filter(propEq(RESOURCE_TYPE_APPLIANCE, 'type')),
      path(['resources'])
    ),

    currentUserStaffer: compose(
      find(r => r.is_current_user),
      path(['resources'])
    ),

    staffersById: (state, getters) => {
      return indexBy(prop('id'), getters.staffers)
    },

    currentStafferId: state => {
      return state.business.current_staffer ? state.business.current_staffer.id : null
    },

    taxRates: state => {
      return compose(
        map(r => {
          return {
            label: ((r.rate === null)
              ? __t('store.business.tax-free')
              : (isNaN(r.rate) ? r.rate : parseFloat(r.rate) + '%')),
            value: r.rate,
          }
        }),
        ifElse(isNil, always([]), identity),
        path(['tax_rates'])
      )(state.pos)
    },

    defaultProductTaxRate: state => {
      return taxRateProp => {
        return compose(
          ifElse(isNil, always(null), prop(taxRateProp)),
          find(propEq(true, 'default_for_product')),
          ifElse(isNil, always([]), identity),
          path(['tax_rates'])
        )(state.pos)
      }
    },

    serviceCategoriesVariantProxy: state => {
      let categories = clone(state.serviceCategories)

      state.serviceCategories.forEach((category, i) => {
        let services = []

        category.services.forEach(({ variants, name, color, resources, is_traveling_service }) => {
          variants.forEach(variant => {
            services.push({
              ...variant,
              name,
              color,
              resources,
              is_traveling_service,
            })
          })
        })

        categories[i].services = services
      })

      return categories
    },

    serviceCategoriesVariantProxyNoCombo: (state, getters) => {
      return getters.serviceCategoriesVariantProxy.map((category) => {
        const services = category.services.filter(service => !service.combo_children.length)
        return {
          ...category,
          services,
        }
      })
    },

    serviceCategoriesNoCombo: state => {
      return state.serviceCategories.map(category => {
        const services = category.services.filter(service => !service.combo_type)
        return {
          ...category,
          services,
        }
      })
    },

    servicesNoComboNoApplianceOnly: state => {
      return state.serviceCategories.reduce((categoryAcc, { services }) => {
        return [
          ...categoryAcc,
          ...services.map(service => {
            return {
              ...service,
              variants: service.variants.filter(variant => variant.staffers?.length),
            }
          }).filter(service => service.variants.length && !service.combo_type),
        ]
      }, [])
    },

    subbookingServices: state => {
      return state.serviceCategories.reduce((services, category) => {
        category.services.forEach(service => {
          service.variants.forEach(variant => {
            services.push({
              id: service.id,
              name: service.name,
              variant: {
                id: variant.id,
                price: variant.price,
                service_price: variant.service_price,
                type: variant.type,
                duration: variant.duration,
                label: variant.label,
                combo_children: variant.combo_children,
                staffers: variant.staffers,
              },
              gap_time: service.gap_time,
              category_name: category.name,
              service_category_id: category.id,
              resources: service.resources,
              color: service.color,
              combo_type: service.combo_type,
              partner_app_data: service.partner_app_data,
              no_show_protection: service.no_show_protection,
            })
          })
        })
        return services
      }, [])
    },

    serviceComboParents: (state, getters) => (variantIds) => {
      return getters.subbookingServices.reduce((prev, curr) => {
        variantIds.forEach(id => {
          const isPartOfCombo = curr.variant.combo_children.find(variant => variant.service_variant.id === id)
          if (isPartOfCombo) {
            prev.add(curr)
          }
        })
        return prev
      }, new Set())
    },

    workingDays: state => {
      const working = Array(7)
      state.business.open_hours.map(hours => {
        working[hours.day_of_week] = true
      })
      return working
    },

    /**
     * @deprecated Use the getter directly from subscription module
     */
    subscription: (state, getters, rootState, rootGetters) => {
      return rootGetters['subscription/subscription']
    },

    /**
     * @deprecated Use the getter directly from subscription module
     */
    hasActiveSubscription: (state, getters, rootState, rootGetters) => {
      return rootGetters['subscription/hasActiveSubscription']
    },

    restrictedBySubscription: state => {
      return [
        BUSINESS_STATUSES.TRIAL_BLOCKED,
        BUSINESS_STATUSES.BLOCKED_OVERDUE,
        BUSINESS_STATUSES.SUSPENDED,
        BUSINESS_STATUSES.CHURNED,
        BUSINESS_STATUSES.BUSINESS_INVALID,
      ].includes(state.business.status)
    },

    businessStatus: state => state.business.status,

    boostCommission: state => path(['commission_val'], state.boostStatusData),

    boostStatus: state => path(['boost_status', 'status'], state.boostStatusData),

    isBoostBlocked: state => {
      const status = path(['boost_status', 'status'], state.boostStatusData)
      return status === BUSINESS_BOOST_STATUSES.DISABLED
    },

    isBoostAutoSuspension: state => path(['boost_status', 'auto_suspension'], state.boostStatusData),

    boostAvailable: state => {
      if (state.business.status === BUSINESS_STATUSES.TRIAL) return false

      return state.boostAvailable
    },

    isBusinessHiddenAndNotPostopned: state => state.business.visible_delay_till === null && state.business.visible === false,

    paymentSource: state => state.business.payment_source,

    business: state => state.business,

    businessId: state => state.business?.id,

    stripeAccountStatus: state => state.stripeAccount?.status,

    stripeAccountLocation: state => state.stripeAccount?.location,

    isStripeAccountTurnedOff: (state, getters) => getters.stripeAccountStatus ? getters.stripeAccountStatus === KYC_STATUSES.TURNED_OFF : true,

    isPayByAppOnStripe: state => state.pos?.force_stripe_pba,

    stripePayoutLimits: state => state.stripePayoutLimits,

    isMissingSubscription: state => [BUSINESS_STATUSES.TRIAL, BUSINESS_STATUSES.TRIAL_BLOCKED].includes(state.business.status),

    bcrAvailable: (state, getters, rootState) => {
      return state.stripeAccount && state.pos?.stripe_terminal_enabled && rootState.routingConfig?.config?.pos__stripe_terminal
    },

    isBcrConnectAvailable: (state) => {
      if (!state.stripeAccount) {
        return false
      }

      const { blocked, status, onboarding_status } = state.stripeAccount

      return !blocked
        && status === KYC_STATUSES.VERIFIED
        && Object.values(ONBOARDING_STATUSES).includes(onboarding_status)
    },

    posPayByAppStatus: state => state.pos?.pay_by_app_status || PAY_BY_APP_STATUSES.DISABLED,

    isSingleStafferBusiness: (state, getters, rootState, rootGetters) => {
      return getters.staffers.length === 1 && rootGetters['identity/isOwner']
    },

    isUserVerified: state => state.stripeAccount?.status === KYC_STATUSES.VERIFIED || state.pos?.pay_by_app_status === PAY_BY_APP_STATUSES.ENABLED,

    isKycVerificationPending: state => {
      if (state.pos.force_stripe_pba) {
        return state.stripeAccount?.status === KYC_STATUSES.VERIFICATION_PENDING
      }

      return [
        PAY_BY_APP_STATUSES.PENDING,
        PAY_BY_APP_STATUSES.PENDING_ADYEN_APPROVAL,
      ].includes(state.pos?.pay_by_app_status)
    },

    isStripeBcrFeesAccepted: state => {
      return state.stripeAccountHolderInfo?.settings.stripe.bcr_fees_accepted === true
    },

    isStripePbaFeesAccepted: state => {
      return state.stripeAccountHolderInfo?.settings.stripe.pba_fees_accepted === true
    },

    isStripeTapToPayFeesAccepted: state => {
      return state.stripeAccountHolderInfo?.settings.stripe.tap_to_pay_fees_accepted === true
    },

    isFrenchCertificationEnabled: state => state.certificationSettings?.french_certification_enabled || false,

    hasExtendedTaxInfo: state => state.certificationSettings?.extended_tax_info || false,

    hasStripeAccount: state => state.pos?.has_stripe_account,

    doesStripeUserNeedToken: state => state.pos?.stripe_account_tokens_enabled,

    shouldUseTapToPay: state => state.pos?.tap_to_pay_enabled,

    areStripeChargesEnabled: state => state.stripeAccount?.charges_enabled,

    areStripePayoutsEnabled: state => state.stripeAccount?.payouts_enabled,

    isBooksyGiftCardPaymentType: state => state.pos?.payment_types.some(type => type.code === PAYMENT_TYPE.BOOKSY_GIFT_CARD),

    isBusinessBookingSemiAuto: state => state.business.booking_mode === BOOKING_MODES.SEMIAUTOMATIC,

    hasEcommerceEnabled: (state, _, __, rootGetters) => {
      const eppo = useEppo()

      // Checks general Ecommerce access
      if (!eppo.ff(FEATURE_FLAGS.Feature_Ecommerce)) {
        return false
      }

      // Checks StorePermission feature availability
      if (eppo.ff(FEATURE_FLAGS.Feature_StorePermissions)) {
        return state.business.current_staffer?.has_store_available ?? false
      }

      // Fallback based on the user access level
      return rootGetters['identity/isReceptionOrHigher']
    },

    isBooksyPayAllowed: (state) => {
      return state.booksyPaySettings?.can_be_enabled || false
    },

    isBooksyPayEnabled: state => state.booksyPaySettings?.enabled || false,

    shouldUseBooksyPay: (state, getters, rootState, rootGetters) => {
      return rootGetters['routingConfig/shouldUseBooksyPay'] && (getters.isBooksyPayEnabled || getters.isBooksyPayAllowed)
    },

    platformFeeMode: state => state.pos?.platform_fee?.mode || PLATFORM_FEE_MODES.SPLIT,

    isPlatformFeeEnabled: state => state.pos?.platform_fee_feature_available || false,

    defaultDepositFixedValue: state => state.business?.default_deposit_fixed_value || null,

    shouldBySubscription: (state, getters) => {
      const eppo = useEppo()
      if (eppo.ff(FEATURE_FLAGS.Feature_AllowPaymentsForTrialBusiness)) {
        return !getters.hasStripeAccount && getters.isMissingSubscription
      }

      return getters.isMissingSubscription
    },

    isAutoChargePbaEnabled: state => state.pos?.is_auto_charge_pba_enabled || false,
  },
}
